// Thin API layer talking to the FastAPI backend.
// Handles JWT-in-cookie auth (withCredentials) plus a Bearer token fallback
// (kept in localStorage for cross-domain resilience during preview).
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND}/api`;

const TOKEN_KEY = "apicon2026.token";

export const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
};
const setToken = (t) => {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
};

export const http = axios.create({
  baseURL: API_BASE,
  // We authenticate via the Authorization: Bearer header (token stored in
  // localStorage). Credentialed (cookie) requests are incompatible with the
  // wildcard CORS origin used in deploys, and browsers will silently block
  // the response otherwise.
  withCredentials: false,
});
http.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// ---------- shape helpers ----------
const snakeToCamel = (s) => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
const camelToSnake = (s) => s.replace(/([A-Z])/g, "_$1").toLowerCase();

const deepConvert = (obj, fn) => {
  if (Array.isArray(obj)) return obj.map((v) => deepConvert(v, fn));
  if (obj && typeof obj === "object" && !(obj instanceof Date)) {
    const out = {};
    for (const [k, v] of Object.entries(obj)) out[fn(k)] = deepConvert(v, fn);
    return out;
  }
  return obj;
};
export const toCamel = (o) => deepConvert(o, snakeToCamel);
export const toSnake = (o) => deepConvert(o, camelToSnake);

export const readError = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((x) => (x && typeof x.msg === "string" ? x.msg : JSON.stringify(x))).join(" ");
  return JSON.stringify(d);
};

// ---------- auth ----------
export const authApi = {
  async register(input) {
    const { data } = await http.post("/auth/register", input);
    // Phone OTP verification is skipped — backend returns { user, token }
    // immediately so the client can hydrate the session in one step.
    if (data.token) setToken(data.token);
    return { user: toCamel(data.user), token: data.token };
  },
  async resendOtp(email) {
    const { data } = await http.post("/auth/resend-otp", { email });
    return { email: data.email, otpCode: data.otp_code, smsSent: !!data.sms_sent };
  },
  async verifyOtp(email, code) {
    const { data } = await http.post("/auth/verify-otp", { email, code });
    if (data.token) setToken(data.token);
    return toCamel(data.user);
  },
  async login(email, password) {
    const { data } = await http.post("/auth/login", { email, password });
    if (data.token) setToken(data.token);
    return toCamel(data.user);
  },
  async logout() {
    try {
      await http.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setToken(null);
  },
  async me() {
    const { data } = await http.get("/auth/me");
    return toCamel(data);
  },
  async forgotPassword(email) {
    const { data } = await http.post("/auth/forgot-password", { email });
    return data;
  },
  async resetPassword(email, code, newPassword) {
    const { data } = await http.post("/auth/reset-password", {
      email,
      code,
      new_password: newPassword,
    });
    if (data.token) setToken(data.token);
    return toCamel(data.user);
  },
};

// ---------- payments ----------
export const paymentApi = {
  async submit({ packageId, slot, txnRef }) {
    const { data } = await http.post("/payments/submit", {
      package_id: packageId,
      slot,
      txn_ref: txnRef,
    });
    return toCamel(data);
  },
  async createRazorpayOrder({ packageId, slot }) {
    const { data } = await http.post("/payments/razorpay/create-order", {
      package_id: packageId,
      slot,
    });
    // Backend returns snake_case fields we deliberately keep as-is because
    // the Razorpay checkout SDK expects `order_id`, `key_id`, etc.
    return data;
  },
  async verifyRazorpay({ razorpayOrderId, razorpayPaymentId, razorpaySignature, packageId, slot }) {
    const { data } = await http.post("/payments/razorpay/verify", {
      razorpay_order_id: razorpayOrderId,
      razorpay_payment_id: razorpayPaymentId,
      razorpay_signature: razorpaySignature,
      package_id: packageId,
      slot,
    });
    return toCamel(data);
  },
  async downloadInvoice() {
    const { data } = await http.get("/payments/invoice", {
      responseType: "blob",
    });
    return data;
  },
};

// ---------- admin ----------
export const adminApi = {
  async listUsers() {
    const { data } = await http.get("/admin/users");
    return data.map(toCamel);
  },
  async exportRegistrations() {
    const { data } = await http.get("/admin/registrations/export", {
      responseType: "blob",
    });
    return data;
  },
  async setPaymentStatus(userId, status) {
    const { data } = await http.post(`/admin/users/${userId}/payment-status`, {
      status,
    });
    return toCamel(data);
  },
};

// ---------- committee ----------
const makeCollection = (name) => ({
  async list() {
    const { data } = await http.get(`/${name}`);
    return data;
  },
  async create(input) {
    const { data } = await http.post(`/admin/${name}`, input);
    return data;
  },
  async update(id, input) {
    const { data } = await http.patch(`/admin/${name}/${id}`, input);
    return data;
  },
  async remove(id) {
    await http.delete(`/admin/${name}/${id}`);
    return true;
  },
  async reorder(ids) {
    await http.post(`/admin/${name}/reorder`, { ids });
    return true;
  },
});

export const committeeApi = makeCollection("committee");

// ---------- gallery ----------
export const galleryApi = {
  ...makeCollection("gallery"),
  async bulkUpload(files, category = "General", onProgress) {
    const form = new FormData();
    for (const f of files) form.append("files", f);
    form.append("category", category);
    const { data } = await http.post("/admin/gallery/bulk-upload", form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) {
          onProgress(Math.round((evt.loaded * 100) / evt.total));
        }
      },
    });
    return data; // { created, errors, count }
  },
};

// ---------- videos ----------
export const videosApi = makeCollection("videos");

// ---------- blog posts ----------
export const postsApi = makeCollection("posts");

// ---------- site settings (hero) ----------
export const settingsApi = {
  async get() {
    const { data } = await http.get("/settings");
    return toCamel(data);
  },
  async update(input) {
    const { data } = await http.put("/admin/settings", toSnake(input));
    return toCamel(data);
  },
};

// ---------- site images (hero mosaic, logos, page asides) ----------
export const siteImagesApi = {
  async upload(slot, file, onProgress) {
    const form = new FormData();
    form.append("file", file);
    const { data } = await http.post(`/admin/images/${slot}`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) {
          onProgress(Math.round((evt.loaded * 100) / evt.total));
        }
      },
    });
    return data; // { slot, url }
  },
  async clear(slot) {
    const { data } = await http.delete(`/admin/images/${slot}`);
    return data;
  },
  async uploadPhoto(file) {
    // Generic single-photo upload — used by committee & blog editors.
    const form = new FormData();
    form.append("file", file);
    const { data } = await http.post("/admin/upload-photo", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data; // { url }
  },
};

// ---------- admin abstracts ----------
export const adminAbstractsApi = {
  async resendAll() {
    const { data } = await http.post("/admin/abstracts/resend");
    return data;
  },
  async resendStatus() {
    const { data } = await http.get("/admin/abstracts/resend-status");
    return toCamel(data);
  },
  async list() {
    const { data } = await http.get("/admin/abstracts");
    return data.map(toCamel);
  },
  async exportExcel() {
    const { data } = await http.get("/admin/abstracts/export", {
      responseType: "blob",
    });
    return data;
  },
  async setStatus(id, status) {
    const { data } = await http.post(`/admin/abstracts/${id}/status`, { status });
    return toCamel(data);
  },
};

// ---------- accompanying persons ----------
export const accompanyingApi = {
  async list() {
    const { data } = await http.get("/accompanying");
    return {
      items: (data.items || []).map(toCamel),
      unitAmount: data.unit_amount,
      pendingCount: data.pending_count,
      pendingTotal: data.pending_total,
    };
  },
  async create({ name, age, gender, relation }) {
    const { data } = await http.post("/accompanying", {
      name,
      age: age === "" || age === null ? null : Number(age),
      gender: gender || null,
      relation: relation || "",
    });
    return toCamel(data);
  },
  async remove(id) {
    const { data } = await http.delete(`/accompanying/${id}`);
    return data;
  },
  async createRazorpayOrder() {
    const { data } = await http.post("/accompanying/razorpay/create-order");
    return data;
  },
  async verifyRazorpay({ razorpayOrderId, razorpayPaymentId, razorpaySignature }) {
    const { data } = await http.post("/accompanying/razorpay/verify", {
      razorpay_order_id: razorpayOrderId,
      razorpay_payment_id: razorpayPaymentId,
      razorpay_signature: razorpaySignature,
    });
    return data;
  },
};

// ---------- abstracts ----------
export const abstractsApi = {
  async eligibility() {
    const { data } = await http.get("/abstracts/eligibility");
    return toCamel(data);
  },
  async list() {
    const { data } = await http.get("/abstracts");
    return data.map(toCamel);
  },
  async create(input) {
    const { data } = await http.post("/abstracts", toSnake(input));
    return toCamel(data);
  },
  async update(id, input) {
    const { data } = await http.patch(`/abstracts/${id}`, toSnake(input));
    return toCamel(data);
  },
  async remove(id) {
    await http.delete(`/abstracts/${id}`);
    return true;
  },
  async uploadFile(id, file) {
    const form = new FormData();
    form.append("file", file);
    const { data } = await http.post(`/abstracts/${id}/upload`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
};
